import React, { ReactElement, useState } from "react";
import { cloneDeep } from "lodash";
import { utils, writeFileXLSX } from "xlsx";

import { ExpandedState, flexRender, GroupingState, Row, RowModel, useTable } from "@tanstack/react-table";
import { FileSpreadsheet } from "lucide-react";
import { tableGrouperFeatures } from "./features";
import { exportToXlsx2 } from "../../../components/src/common/exportToXlsx2";
import { PerformanceDataExcel, PerformanceExcel } from "../../../portal/src/containers/performance/interfaces";
import { accountReturn, benchmarkReturn, diffReturn } from "../../../portal/src/containers/performance/functions";

const NULL_PERFORMANCE_DATA: PerformanceDataExcel = {
    isNull: true,
    dateSeries: [],
    startDate: "1970-01-01",
    endDate: "2999-12-24",
    benchmarksById: {},
    accountSeriesById: {},
    accountPositionsById: {},
    modelSeriesById: {},
    useModelSeriesAsMain: true
};

export interface Props {
    columns: any[];
    data: any;
    expanded: { [key: string]: boolean };
    groupBy?: string[];
    hiddenColumns?: string[];
    compactMode?: boolean;
    captionColumn?: string;
    sortBy?: {
        id: string;
        desc: boolean;
    }[]; //must be memoized
    showExcelButton?: boolean;
    showGroupByBox?: boolean;
    customColumnSize?: boolean;
    performanceData?: PerformanceDataExcel;
    // When true, the parenthesis count next to a grouped row shows the number of leaf descendants
    // (i.e. all bottom-level rows under that node). When false (default) it shows the number of
    // immediate child rows, preserving the existing behavior for legacy consumers.
    countLeafDescendants?: boolean;
}

function countLeafRows<TData extends Record<string, any>>(row: Row<typeof tableGrouperFeatures, TData>): number {
    if (!row.subRows || row.subRows.length === 0) return 1;
    let total = 0;
    for (const child of row.subRows) total += countLeafRows(child);
    return total;
}

function getTableExcelJson<TData extends Record<string, any>>(
    rowModel: RowModel<typeof tableGrouperFeatures, TData>,
    performanceData: PerformanceDataExcel
): PerformanceExcel[] {
    // extract data
    const {
        startDate,
        endDate,
        dateSeries,
        benchmarksById,
        accountSeriesById,
        accountPositionsById,
        modelSeriesById,
        useModelSeriesAsMain
    } = performanceData;

    // dates
    const dStartDate = new Date(startDate);
    const dEndDate = new Date(endDate);
    const mtdStartDate = new Date(dEndDate.getFullYear(), dEndDate.getMonth(), 0);
    // December month 11 in JavaScript!!!!
    const ytdStartDate = new Date(dEndDate.getFullYear() - 1, 11, 31);

    const compare = (a: Row<typeof tableGrouperFeatures, TData>, b: Row<typeof tableGrouperFeatures, TData>) => {
        if (a.subRows?.length) {
            const field = "level" + a.depth.toString();

            if (a.original[field] < b.original[field]) return -1;
            if (a.original[field] > b.original[field]) return 1;
            return 0;
        } else {
            const aValue: string = a.getValue("instrumentName");
            const bValue: string = b.getValue("instrumentName");

            if (aValue < bValue) return -1;
            if (aValue > bValue) return 1;
            return 0;
        }
    };

    const out: PerformanceExcel[] = [];
    const walk = (rows: Row<typeof tableGrouperFeatures, TData>[]) => {
        for (const row of rows) {
            let name = "";
            if (row.subRows?.length) {
                if (row.depth === 0) {
                    name = row.original["root"];
                } else if (row.depth === 1) {
                    name = row.original["level1"];
                } else if (row.depth === 2) {
                    name = row.original["level2"];
                } else if (row.depth === 3) {
                    name = row.original["level3"];
                } else if (row.depth === 4) {
                    name = row.original["level4"];
                }
            } else {
                name = row.getValue("instrumentName");
            }

            out.push({
                name: name,
                firstTradeDate: row.getValue("firstTradeDate"),
                valueSek: row.getValue("valueSEK"),
                accountReturnMtd: accountReturn(row, dateSeries, mtdStartDate, dEndDate, accountSeriesById, false),
                benchmarkReturnMtd: benchmarkReturn(
                    row,
                    dateSeries,
                    mtdStartDate,
                    dEndDate,
                    benchmarksById,
                    accountPositionsById,
                    modelSeriesById,
                    useModelSeriesAsMain,
                    false
                ),
                differenceReturnMtd: diffReturn(
                    row,
                    dateSeries,
                    mtdStartDate,
                    dEndDate,
                    benchmarksById,
                    accountSeriesById,
                    accountPositionsById,
                    modelSeriesById,
                    useModelSeriesAsMain,
                    false
                ),
                accountReturnYtd: accountReturn(row, dateSeries, ytdStartDate, dEndDate, accountSeriesById, false),
                benchmarkReturnYtd: benchmarkReturn(
                    row,
                    dateSeries,
                    ytdStartDate,
                    dEndDate,
                    benchmarksById,
                    accountPositionsById,
                    modelSeriesById,
                    useModelSeriesAsMain,
                    false
                ),
                differenceReturnYtd: diffReturn(
                    row,
                    dateSeries,
                    ytdStartDate,
                    dEndDate,
                    benchmarksById,
                    accountSeriesById,
                    accountPositionsById,
                    modelSeriesById,
                    useModelSeriesAsMain,
                    false
                ),
                accountReturnPeriod: accountReturn(row, dateSeries, dStartDate, dEndDate, accountSeriesById, true),
                benchmarkReturnPeriod: benchmarkReturn(
                    row,
                    dateSeries,
                    dStartDate,
                    dEndDate,
                    benchmarksById,
                    accountPositionsById,
                    modelSeriesById,
                    useModelSeriesAsMain,
                    true
                ),
                differenceReturnPeriod: diffReturn(
                    row,
                    dateSeries,
                    dStartDate,
                    dEndDate,
                    benchmarksById,
                    accountSeriesById,
                    accountPositionsById,
                    modelSeriesById,
                    useModelSeriesAsMain,
                    true
                )
            });

            // sort to mimic behavior in tanstack table
            if (row.subRows?.length) walk(row.subRows.sort(compare));
        }
    };

    walk(rowModel.rows);
    return out;
}

export const TableGrouper = ({
    columns,
    data,
    expanded,
    groupBy = [],
    hiddenColumns = [],
    compactMode,
    captionColumn,
    sortBy,
    showExcelButton = false,
    showGroupByBox = false,
    customColumnSize = false,
    performanceData = NULL_PERFORMANCE_DATA,
    countLeafDescendants = false
}: Props): ReactElement => {
    const [grouping, setGrouping] = useState<GroupingState>(groupBy);
    const [expandedState, setExpanded] = useState<ExpandedState>(expanded);
    const columnVisibility = Object.fromEntries(hiddenColumns.map((x) => [x, false]));

    const table = useTable({
        features: tableGrouperFeatures,
        data,
        columns,
        state: {
            columnVisibility,
            grouping,
            expanded: expandedState,
            sorting: sortBy
        },
        autoResetExpanded: false,
        onGroupingChange: setGrouping,
        onExpandedChange: setExpanded,
        getSubRows: (row: any) => row.subRows
    });

    let rowData: any[] = [];
    const performanceExcel: PerformanceExcel[] = [];
    if (rowData.length < 1 && showExcelButton) {
        // if no performance data sent use standard path
        if (performanceData.isNull) {
            const columnHeaders = {};
            columns[0].columns.forEach((column) => {
                columnHeaders[column.accessor] = column.Header;
            });
            table.getRowModel().rows.forEach((row: Row<typeof tableGrouperFeatures, any>) => {
                const groupedRow = cloneDeep(row.original);

                for (const key of Object.keys(groupedRow)) {
                    if (row.id && !row.id.includes(key) && typeof groupedRow[key] === "string") {
                        groupedRow[key] = "";
                    }
                    // rename key from columnHeaders
                    if (columnHeaders[key]) {
                        groupedRow[columnHeaders[key]] = groupedRow[key];
                        delete groupedRow[key];
                    }
                }
                // Not adding expanded rows twice
                if (row.id !== row.index.toString()) {
                    rowData.push(groupedRow);
                }

                if (row.subRows && row.subRows.length && !row.subRows[0].subRows.length) {
                    const subRows: any[] = [];
                    row.subRows.forEach((subRow) => {
                        if (!subRow.getIsGrouped()) {
                            const subRowOriginal = cloneDeep(subRow);
                            for (const key of Object.keys(subRowOriginal)) {
                                if (subRow.id && !subRow.id.includes(key) && typeof subRowOriginal[key] === "string") {
                                    subRowOriginal[key] = "";
                                }
                                // rename key from columnHeaders
                                if (columnHeaders[key]) {
                                    subRowOriginal[columnHeaders[key]] = subRowOriginal[key];
                                    delete subRowOriginal[key];
                                }
                            }
                            subRows.push(subRow.original);
                        }
                    });
                    rowData = rowData.concat(subRows);
                }
            });
        } else {
            // calculate performance data for output to excel since
            // standard aggregations etc. does not work with series

            // traverse grouped model and populate
            const groupedRowModel = table.getGroupedRowModel();
            const tableExcelJson = getTableExcelJson(groupedRowModel, performanceData);
            performanceExcel.push(...tableExcelJson);
        }
    }

    const downloadExcel = () => {
        exportToXlsx2([rowData], "table.xlsx");
    };

    const jsonToExcel = () => {
        // create worksheet
        const ws = utils.json_to_sheet(performanceExcel);
        /* create workbook and export */
        const wb = utils.book_new();
        // append sheet
        utils.book_append_sheet(wb, ws, "performance");
        // try to write
        try {
            writeFileXLSX(wb, "performanceTable.xlsx");
        } catch (e) {
            if (typeof console !== "undefined") console.log(e, performanceExcel);
        }
    };

    return (
        <div className="captor-table">
            {showExcelButton ? (
                <div
                    title="Export to XLSX"
                    style={{ width: "20px", height: "20px", marginLeft: "auto", marginBottom: "0.5rem", cursor: "pointer" }}
                    onClick={() => {
                        if (performanceData.isNull) {
                            downloadExcel();
                        } else {
                            jsonToExcel();
                        }
                    }}
                >
                    <FileSpreadsheet className="Excel" />
                </div>
            ) : null}
            <table className="table table-striped table-xs" role="table">
                <thead>
                    {compactMode
                        ? table.getHeaderGroups().map((headerGroup) => {
                              const totalColumnsSpan = table
                                  .getHeaderGroups()[0]
                                  .headers.map((x) => x.colSpan)
                                  .reduce((acc, curr) => acc + curr, 0);
                              const columnsSpan = headerGroup.headers
                                  .filter((h) => !h.column.getIsGrouped() && h.column.id !== captionColumn)
                                  .map((x) => x.colSpan)
                                  .reduce((acc, curr) => acc + curr, 0);
                              const neededSpan = totalColumnsSpan - columnsSpan;
                              const captionHeader = headerGroup.headers.find((h) => h.column.id === captionColumn);

                              return (
                                  <tr key={headerGroup.id} className="tr" role="row">
                                      {neededSpan > 0 ? (
                                          <th
                                              key={"column0"}
                                              className="th"
                                              colSpan={neededSpan}
                                              style={customColumnSize && captionHeader ? { width: captionHeader.getSize() } : undefined}
                                          ></th>
                                      ) : null}
                                      {headerGroup.headers.map((header) =>
                                          !header.column.getIsGrouped() && header.column.id !== captionColumn ? (
                                              <th
                                                  key={header.id}
                                                  className="th"
                                                  role="columnheader"
                                                  colSpan={header.colSpan}
                                                  style={customColumnSize ? { width: header.getSize() } : undefined}
                                              >
                                                  {header.column.getCanGroup() && showGroupByBox ? (
                                                      // If the column can be grouped, let's add a toggle
                                                      <span className="me-1">
                                                          <input
                                                              id="cb"
                                                              type="checkbox"
                                                              title="Toggle GroupBy"
                                                              checked={header.column.getIsGrouped()}
                                                              onClick={header.column.getToggleGroupingHandler()}
                                                              readOnly
                                                          ></input>
                                                      </span>
                                                  ) : null}
                                                  {flexRender(header.column.columnDef.header, header.getContext())}
                                              </th>
                                          ) : null
                                      )}
                                  </tr>
                              );
                          })
                        : table.getHeaderGroups().map((headerGroup) => (
                              <tr key={headerGroup.id} className="tr" role="row">
                                  {headerGroup.headers.map((header) => (
                                      <th
                                          key={header.id}
                                          className="th"
                                          role="columnheader"
                                          colSpan={header.colSpan}
                                          style={customColumnSize ? { width: header.getSize() } : undefined}
                                      >
                                          {header.column.getCanGroup() && showGroupByBox ? (
                                              // If the column can be grouped, let's add a toggle
                                              <span className="me-1">
                                                  <input
                                                      id="cb"
                                                      type="checkbox"
                                                      title="Toggle GroupBy"
                                                      checked={header.column.getIsGrouped()}
                                                      onClick={header.column.getToggleGroupingHandler()}
                                                      readOnly
                                                  ></input>
                                              </span>
                                          ) : null}
                                          {flexRender(header.column.columnDef.header, header.getContext())}
                                      </th>
                                  ))}
                              </tr>
                          ))}
                </thead>
                <tbody>
                    {table.getRowModel().rows.map((row) => {
                        if (compactMode) {
                            const groupedCells = row.getVisibleCells().filter((c) => c.column.getIsGrouped());
                            //caption cell
                            const captionCell = row
                                .getVisibleCells()
                                .find((c) => c.column.id === captionColumn && !c.getIsPlaceholder() && c.getValue());
                            if (captionCell) {
                                groupedCells.push(captionCell);
                            }
                            // other cells
                            const cells = row
                                .getVisibleCells()
                                .filter((c) => !c.getIsPlaceholder() && !c.getIsGrouped() && c.column.id !== captionColumn);
                            const totalColumnsSpan = table
                                .getHeaderGroups()[0]
                                .headers.map((x) => x.colSpan)
                                .reduce((acc, curr) => acc + curr, 0);
                            return (
                                <tr key={row.id} className="tr" role="row">
                                    <td
                                        key={"group-column" + row.id}
                                        className="td"
                                        role="cell"
                                        style={{ width: 300 }}
                                        colSpan={totalColumnsSpan - cells.length}
                                    >
                                        {groupedCells.map((cell, cellIndex) => {
                                            return cell.getIsPlaceholder() ? null : (
                                                <React.Fragment key={cell.id}>
                                                    <span
                                                        {...{
                                                            onClick: row.getToggleExpandedHandler(),
                                                            style: { cursor: "pointer", paddingRight: "5px", paddingLeft: cellIndex * 10 }
                                                        }}
                                                    >
                                                        {row.subRows.length > 0 ? (row.getIsExpanded() ? "⬇" : "➡") : null}
                                                    </span>
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}{" "}
                                                    {row.subRows.length > 0
                                                        ? "(" + (countLeafDescendants ? countLeafRows(row) : row.subRows.length) + ")"
                                                        : null}
                                                </React.Fragment>
                                            );
                                        })}
                                    </td>

                                    {cells.map((cell) => {
                                        return cell.getIsPlaceholder() ? null : (
                                            <td key={cell.id} className="td" role="cell" style={{ width: cell.column.getSize() }}>
                                                {cell.getIsAggregated()
                                                    ? flexRender(
                                                          cell.column.columnDef.aggregatedCell ?? cell.column.columnDef.cell,
                                                          cell.getContext()
                                                      )
                                                    : flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        } else {
                            return (
                                <tr key={row.id} className="tr" role="row">
                                    {row.getVisibleCells().map((cell) => {
                                        return (
                                            <td key={cell.id} className="td" role="cell" style={{ width: cell.column.getSize() }}>
                                                {cell.getIsGrouped() ? (
                                                    // If it's a grouped cell, add an expander and row count
                                                    <>
                                                        <span
                                                            {...{
                                                                onClick: row.getToggleExpandedHandler(),
                                                                style: { cursor: "pointer", paddingRight: "5px" }
                                                            }}
                                                        >
                                                            {row.getIsExpanded() ? "⬇" : "➡"}
                                                        </span>
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())} (
                                                        {countLeafDescendants ? countLeafRows(row) : row.subRows.length})
                                                    </>
                                                ) : cell.getIsAggregated() ? (
                                                    // If the cell is aggregated, use the Aggregated renderer for cell
                                                    flexRender(
                                                        cell.column.columnDef.aggregatedCell ?? cell.column.columnDef.cell,
                                                        cell.getContext()
                                                    )
                                                ) : cell.getIsPlaceholder() ? null : ( // For cells with repeated values, render null
                                                    // Otherwise, just render the regular cell
                                                    flexRender(cell.column.columnDef.cell, cell.getContext())
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        }
                    })}
                </tbody>
            </table>
        </div>
    );
};
